import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, OnDestroy, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { Attachment, EmbeddedCode, ItemTaskCount, Task, TaskLinkType } from '@jms/model';
import { GeneralSettingService } from '@jms/ui/ui.standalone.services/general.setting.service';
import { TASK_EVIDENCE_OUTCOME_ICONS, TASK_EVIDENCE_OUTCOME_TONES } from '@workbuddy/entities/work.task/evidence.exception';
import { BadgeComponent } from '@workbuddy/ui/components/badge';
import { DateTimeFormatPipe } from '@workbuddy/ui/fields/luxon.calendar';
import { HoverCardComponent } from '@workbuddy/ui/v2/components/hover.card';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { observeVisibleOnce } from '@workbuddy/utils/async';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { TooltipModule } from 'primeng/tooltip';
import { resolveItemFromParams } from '@workbuddy/entities/shared/items';
import { SorTaskCountService, watchSorTaskCount } from '../../services/item.task.count.service';
import { resolveItemEvidenceCard } from '../item.evidence/item.evidence.hover';
import { ItemFinanceCardComponent } from './item.finance.card.component';
import { ItemPhotosCardComponent } from './item.photos.card.component';
import { ItemTasksCardComponent } from './item.tasks.card.component';
import { itemIndicatorsCellBridgeStyles, itemIndicatorsCellStyleId } from './item.indicators.cell.renderer.component.theme';

/**
 * SOR indicators: photos + child tasks (CountByItems), finance docs (row) and the evidence outcome
 * (row; its details from the same CountByItems main task). Photos/tasks defer until visible and
 * re-load on {@link SorTaskCountService} changes; finance and outcome follow row value.
 */
@Component({
  selector: 'item-indicators-cell-renderer',
  template: `
    <div class="indicators-cell">
      @if (!$hidePhotosAndTasks()) {
        <!-- Positioned below the badge, unlike its two siblings: the design draws the arrow on the card's top edge. -->
        <w-badge
          size="xs"
          mode="squared"
          iconLeft="fa-regular fa-image"
          [label]="$photoLabel()"
          [isPlaceholder]="!$photoCount()"
          [pTooltip]="photosCard"
          [tooltipDisabled]="!$photoCount()"
          [autoHide]="false"
          [showDelay]="200"
          tooltipPosition="bottom"
          tooltipStyleClass="wb-dark-tooltip"
          appendTo="body" />

        <w-badge
          size="xs"
          mode="squared"
          iconLeft="fa-regular fa-list-ul"
          [label]="$taskLabel()"
          [isPlaceholder]="!$taskCount()"
          [pTooltip]="tasksCard"
          [tooltipDisabled]="!$taskCount()"
          [autoHide]="false"
          [showDelay]="200"
          tooltipPosition="top"
          tooltipStyleClass="wb-dark-tooltip"
          appendTo="body" />
      }

      @if (!$hideFinance()) {
        <w-badge
          size="xs"
          mode="squared"
          iconLeft="fa-regular fa-circle-dollar"
          [label]="$financeLabel()"
          [isPlaceholder]="$financePlaceholder()"
          [pTooltip]="financeCard"
          [autoHide]="false"
          tooltipPosition="top"
          tooltipStyleClass="wb-dark-tooltip"
          appendTo="body" />
      }

      @if ($evidence(); as evidence) {
        <w-badge
          class="evidence-badge"
          [class.is-success]="evidence.tone === 'success'"
          [class.is-warning]="evidence.tone === 'warning'"
          [class.is-error]="evidence.tone === 'error'"
          size="xs"
          mode="squared"
          [iconLeft]="evidence.icon"
          [pTooltip]="evidenceCard"
          [autoHide]="false"
          [showDelay]="200"
          tooltipPosition="top"
          tooltipStyleClass="wb-dark-tooltip"
          appendTo="body" />
      }
    </div>

    <ng-template #photosCard>
      <item-photos-card [photos]="$photos()" />
    </ng-template>

    <ng-template #tasksCard>
      <item-tasks-card [tasks]="$tasks()" [title]="$tasksTitle()" />
    </ng-template>

    <ng-template #financeCard>
      <item-finance-card
        [purchaseOrder]="$item()?.purchaseOrder ?? null"
        [bill]="$item()?.bill ?? null"
        [invoice]="$item()?.invoice ?? null"
        [invoicingStatus]="$item()?.codeInvoicingStatus ?? null"
        [payable]="$item()?.payable ?? null"
        [suppliedBySubcontractor]="!!$item()?.suppliedBySubcontractor" />
    </ng-template>

    <ng-template #evidenceCard>
      @if ($evidence(); as evidence) {
        <wb-hover-card [title]="evidence.card.title" [rows]="evidence.card.rows" />
      }
    </ng-template>
  `,
  styleUrls: ['./item.indicators.cell.renderer.component.less'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BadgeComponent, TooltipModule, ItemPhotosCardComponent, ItemTasksCardComponent, ItemFinanceCardComponent, HoverCardComponent],
})
export class ItemIndicatorsCellRendererComponent implements ICellRendererAngularComp, OnDestroy {

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly taskCounts = inject(SorTaskCountService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly generalSetting = inject(GeneralSettingService);
  private readonly dates = new DateTimeFormatPipe();
  private readonly dateFormat = `${this.generalSetting.generalSettings?.region?.dateFormat ?? 'dd/MM/yyyy'} ${this.generalSetting.generalSettings?.region?.timeFormat ?? 'HH:mm'}`;

  protected readonly $item = signal<EmbeddedCode | null>(null);
  private readonly $timezone = signal<string | undefined>(undefined);
  protected readonly $count = signal<ItemTaskCount | null>(null);

  // The contractor portal: orders, bills and invoices are the office's documents.
  protected readonly $hideFinance = signal(false);

  // The office Items board shows finance alone (WBS-11904); SOR keeps photos and tasks.
  protected readonly $hidePhotosAndTasks = signal(false);

  protected readonly $tasks = computed<Task[]>(() => this.$count()?.tasks ?? []);

  // The Items board shows the same counter since WBS-12164, so the card names the line's own kind.
  protected readonly $tasksTitle = computed(() => this.$item()?.type === 'SOR' ? 'SOR Tasks' : 'Item Tasks');

  /** Evidence lives on the main item-task; the children are the SOR tasks and carry none. */
  private readonly $mainTask = computed(() => this.$tasks().find(task => task.type === TaskLinkType.Item) ?? null);

  /** Before → During → After, the order the item detail panel lists them in. */
  protected readonly $photos = computed<Attachment[]>(() => {

    const main = this.$mainTask();

    return [...(main?.beforePhotos ?? []), ...(main?.duringPhotos ?? []), ...(main?.afterPhotos ?? [])];
  });

  protected readonly $photoCount = computed(() => this.$photos().length);

  protected readonly $taskCount = computed(() => this.$count()?.total ?? 0);

  /**
   * Financial documents the item is linked to. Documents only — `suppliedBySubcontractor` is a
   * yes/no attribute of the line, never something to tally.
   */
  protected readonly $financeCount = computed(() => {

    const item = this.$item();

    return [item?.purchaseOrder, item?.bill, item?.invoice].filter(Boolean).length;
  });

  /**
   * Empty only when the line has nothing financial to say at all. A subcontractor-supplied line has
   * no document to count yet still carries a finance fact, so it stays filled — the card explains
   * why. The card itself is always available: it always states the subcontractor answer.
   */
  protected readonly $financePlaceholder = computed(() => !this.$financeCount() && !this.$item()?.suppliedBySubcontractor);

  // No outcome stored yet (no backfill, D5) draws no badge rather than a placeholder that would read as a state.
  protected readonly $evidence = computed(() => {

    const outcome = this.$item()?.evidenceOutcome;
    const card = resolveItemEvidenceCard(outcome, this.$tasks(), value => this.dates.transform(value, this.$timezone(), this.dateFormat));
    if (!outcome || !card) return null;

    return { tone: TASK_EVIDENCE_OUTCOME_TONES[outcome], icon: TASK_EVIDENCE_OUTCOME_ICONS[outcome], card };
  });

  // Badge hides a falsy label, and every counter shows its zero — pass the count as text.
  protected readonly $photoLabel = computed(() => String(this.$photoCount()));
  protected readonly $taskLabel = computed(() => String(this.$taskCount()));
  protected readonly $financeLabel = computed(() => String(this.$financeCount()));

  private dispose: (() => void) | null = null;
  private visible = false;

  public constructor() {

    DesignSystem.Mount(inject(DOCUMENT), itemIndicatorsCellStyleId, itemIndicatorsCellBridgeStyles);
  }

  public agInit(params: ICellRendererParams & { hideFinance?: boolean; hidePhotosAndTasks?: boolean; timezone?: (stageId?: string) => string }): void {

    this.$item.set(resolveItemFromParams(params) ?? null);
    this.$hideFinance.set(!!params.hideFinance);
    this.$hidePhotosAndTasks.set(!!params.hidePhotosAndTasks);
    this.$timezone.set(params.timezone?.(params.data?.workOrders?._id) ?? this.generalSetting.generalSettings?.region?.timezone);

    // Nothing left to count: the Items board projects no evidence outcome either.
    const itemId = this.$item()?._id;
    if (!itemId || this.$hidePhotosAndTasks()) return;

    this.dispose = observeVisibleOnce(this.el.nativeElement, () => {
      this.visible = true;
      void this.load();
    });

    watchSorTaskCount(
      this.taskCounts,
      () => this.$item()?._id,
      () => this.visible,
      () => void this.load(),
      this.destroyRef,
    );
  }

  public refresh(): boolean {

    // Recreate on grid data reload (paired with SorTaskCountService.clear()).
    return false;
  }

  public ngOnDestroy(): void {

    this.dispose?.();
  }

  private async load(): Promise<void> {

    const itemId = this.$item()?._id;
    if (!itemId) return;

    this.$count.set(await this.taskCounts.getCount(itemId));
  }
}

/**
 * Column value for the indicators cell: every row-derived finance fact the hover card states, and the evidence outcome. The
 * column is virtual, so without this its value never changes and AG's row-diff would skip refresh()
 * on a finance edit. Photo / task counters refresh via SorTaskCountService.changes$ or cell recreate.
 */
export function itemIndicatorsCellValue(params: { data?: unknown; colDef?: { field?: string } }): string {

  const item = resolveItemFromParams(params as unknown as ICellRendererParams);
  if (!item) return '';

  return [item.suppliedBySubcontractor ? 'sub' : '', item.codeInvoicingStatus ?? '', String(item.payable ?? ''), item.purchaseOrder?._id ?? '', item.bill?._id ?? '', item.invoice?._id ?? '', item.evidenceOutcome ?? ''].join('|');
}
